import { Link } from "react-router-dom";
import { Zap, ArrowLeft, ShieldAlert } from "lucide-react";
import Seo from "@/components/Seo";

export default function Legal() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="legal-page">
      <Seo
        title="Content & Copyright Policy"
        description="Videoileo's content and copyright policy. You are responsible for your content — do not generate or publish copyrighted material you do not own or have a license to use."
        path="/legal"
      />
      {/* Nav */}
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-blue-600 flex items-center justify-center">
              <Zap className="h-4 w-4 text-white" fill="white" />
            </div>
            <span className="font-head font-extrabold text-lg tracking-tight">Videoileo</span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Legal</p>
        <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight mb-3">Content & Copyright Policy</h1>
        <p className="text-zinc-400 mb-10">Last updated: June 2026. Please read this before creating or publishing content.</p>

        <div className="flex flex-wrap gap-2 mb-10 border-b border-white/10 pb-4">
          <Link to="/terms" className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-400 hover:text-white transition-colors">Terms of Service</Link>
          <Link to="/privacy" className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-400 hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/acceptable-use" className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-400 hover:text-white transition-colors">Acceptable Use</Link>
          <span className="text-xs px-3 py-1.5 rounded-full border border-blue-500 bg-blue-600/15 text-white">Content &amp; Copyright</span>
        </div>

        {/* Highlighted copyright disclosure */}
        <div className="border border-red-500/30 bg-red-500/10 rounded-md p-6 mb-10" data-testid="copyright-disclosure">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-6 w-6 text-red-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-head text-lg font-semibold mb-2">No copyrighted material</h2>
              <p className="text-sm text-zinc-300 leading-relaxed">
                You may <strong>not</strong> use Videoileo to create, upload, generate, or publish any content that
                infringes the intellectual property rights of others. This includes copyrighted scripts, music, video
                footage, images, brand assets, logos, trademarks, or any material you do not own or have an explicit
                license to use. You are solely responsible for ensuring that every video, voiceover, visual, caption,
                and topic you produce complies with all applicable copyright and trademark laws.
              </p>
            </div>
          </div>
        </div>

        <Section title="1. Acceptable use">
          You agree to use Videoileo only for lawful purposes and in accordance with this policy. You will not use the
          service to create content that is defamatory, hateful, deceptive, infringing, or that violates the rights or
          privacy of any person or third party.
        </Section>

        <Section title="2. Your responsibility for content">
          All scripts, voiceovers, images, and metadata are generated based on the prompts and topics you provide. You
          are fully responsible for the inputs you supply and the outputs you choose to download, schedule, or publish.
          Before publishing to any platform, you must verify that your content does not infringe any third-party
          copyright, trademark, publicity, or privacy rights.
        </Section>

        <Section title="3. AI-generated content">
          AI-generated scripts and visuals may unintentionally resemble existing works. Videoileo does not guarantee that
          generated output is free of third-party rights. It is your responsibility to review, edit, and clear any
          content before commercial or public use.
        </Section>

        <Section title="4. Platform compliance">
          When scheduling or publishing to YouTube, TikTok, Instagram, or any other platform, you must also comply with
          that platform's own terms of service, community guidelines, and copyright policies. Repeated copyright
          violations may result in strikes or termination of your accounts on those platforms.
        </Section>

        <Section title="5. Voice & likeness">
          You may not generate voiceovers that imitate a real person's voice or likeness without their explicit consent.
          Synthetic voices must not be used to impersonate, deceive, or mislead.
        </Section>

        <Section title="6. Disclaimer of liability">
          Videoileo is provided "as is" without warranties of any kind. To the maximum extent permitted by law, we
          disclaim all liability for any claims, damages, or losses arising from your use of generated content,
          including any copyright or trademark infringement claims brought against you.
        </Section>

        <Section title="7. Not affiliated with social platforms">
          Videoileo is an independent tool and is not affiliated with, endorsed by, or sponsored by YouTube, TikTok,
          Instagram, or their parent companies. All trademarks belong to their respective owners.
        </Section>

        <p className="text-xs text-zinc-600 mt-10">
          By using Videoileo you acknowledge that you have read, understood, and agreed to this Content & Copyright
          Policy.
        </p>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-head text-lg font-semibold mb-2">{title}</h2>
      <p className="text-sm text-zinc-400 leading-relaxed">{children}</p>
    </section>
  );
}
